// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TreeListItem.tsx
 * @input Uses React, StyleX, theme tokens, TreeListBranches
 * @output Exports TreeListItem component (internal, not publicly exported)
 * @position Internal implementation; consumed by TreeList.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TreeList/TreeList.doc.mjs
 * - /packages/core/src/TreeList/TreeList.tsx
 * - /packages/cli/assets/templates/blocks/components/TreeList/ (showcase blocks)
 */
import { useId, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, spacingVars, durationVars, easeVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { useIcon } from "../Icon/index.js";
import { mergeProps, rtlStyles } from "../utils/index.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { TreeListBranches } from "./TreeListBranches.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  contentWrapper: {
    kaIpWk: "xh6dtrn",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kg3NbH: "xf314gf",
    kI3sdo: "x1a2a7pz",
    kVQacm: "xb3r6kr",
    kVAEAm: "x1n2onr6",
    kB7OPa: "x9f619",
    k9WMMc: "x1yc453h",
    keTefX: "x12vn87o",
    $$css: true
  },
  interactive: {
    kkrTdU: "x1ypdohk",
    k1ekBW: "x12qzo2w",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kKwaWg: "x1ilzqfv xq8i9tn",
    $$css: true
  },
  focusVisibleOutline: {
    kI3sdo: "x11h0toi x1irc7jg",
    kInvED: "x1g710d4 xdjuwb3",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    kfzvcC: "x47corl",
    $$css: true
  },
  selected: {
    kWkggS: "xgcxg3y",
    $$css: true
  },
  description: {
    kMwMTN: "xv1l7n4",
    $$css: true
  }
};
const densityStyles = {
  compact: {
    k8WAf4: "xu0wf1k",
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  },
  balanced: {
    k8WAf4: "xce4md1",
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  },
  spacious: {
    k8WAf4: "x8o8v82",
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  }
};
const descriptionSizeStyles = {
  compact: {
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  },
  balanced: {
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  },
  spacious: {
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  }
};

// `CSSProperties` has no index signature for custom properties, so the row's
// inline style — which publishes the computed indent distance as the private
// `--_tree-indent` — needs this augmentation to typecheck.

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

export function TreeListItem({
  id,
  label,
  description,
  startContent,
  endContent,
  onClick,
  href,
  target,
  isDisabled = false,
  isSelected = false,
  hasChildren,
  nestedLevel,
  isLast,
  ancestorsIsLast,
  isExpanded,
  onToggle,
  density,
  variant,
  renderedChildren,
  posInSet,
  setSize,
  isTabbable
}) {
  const t = useTranslator();
  const chevronRightIcon = useIcon('chevronRight');
  const labelId = useId();
  const descriptionId = useId();
  const LinkComponent = useLinkComponent();
  const isInteractive = onClick != null || href != null;
  const handleToggle = useMemo(() => hasChildren && onToggle != null ? e => {
    e.stopPropagation();
    onToggle(id);
  } : undefined, [hasChildren, onToggle, id]);
  const handleClick = useMemo(() => {
    if (onClick != null || hasChildren && onToggle != null) {
      return e => {
        if (isDisabled) {
          return;
        }
        const el = e.target;
        if (el.closest('button, a, input, select, textarea')) {
          return;
        }
        if (onClick != null) {
          onClick(e);
        } else if (hasChildren && onToggle != null) {
          onToggle(id);
        }
      };
    }
    return undefined;
  }, [onClick, hasChildren, onToggle, id, isDisabled]);

  // Per-level indent distance. The per-level step is the public, themeable
  // `--tree-list-indent` lever (default `--spacing-4`, set on the tree-list
  // root). Leaves add a fixed chevron-column offset (chevron width + gap) so
  // their labels line up with sibling parents' labels; that offset is tied to
  // the chevron's own dimensions, not the indent step, so it does not scale
  // with the lever. Published as the private `--_tree-indent` and consumed by
  // `contentWrapper`'s stylesheet `margin-inline-start` (kept out of the inline
  // style so the theme layer can override it — see #4308).
  const indentDistance = hasChildren ? `calc(${nestedLevel} * var(--tree-list-indent))` : `calc(${nestedLevel} * var(--tree-list-indent) + ${spacingVars['--spacing-4']} + ${spacingVars['--spacing-2']})`;
  const indentStyle = {
    '--_tree-indent': indentDistance
  };
  const labelAndDescription = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      id: labelId,
      ...mergeProps(
      // Stable theme target for the item's label text. The label carries no
      // themeable handle today, so a theme can only reach it through a
      // fragile structural selector (a content button's first span). This
      // adds an `astryx-tree-list-item-label` class and reflects the row's
      // `selected` state so a theme can, e.g., bold just the selected
      // item's label via `defineTheme`.
      themeProps('tree-list-item-label', {
        selected: isSelected ? 'selected' : null
      }), {
        className: "x1tgivj0"
      }),
      children: label
    }), description != null && /*#__PURE__*/_jsx("span", {
      id: descriptionId,
      ...stylex.props(styles.description, descriptionSizeStyles[density]),
      children: description
    })]
  });
  const chevronIcon = /*#__PURE__*/_jsx("span", {
    ...stylex.props(rtlStyles.mirror),
    children: /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x78zum5 x11xpdln xuedmi6 xlr8y92 x7p49u4"
        },
        1: {
          className: "x78zum5 x11xpdln xuedmi6 xlr8y92 x1iffjtl"
        }
      }[!!isExpanded << 0],
      children: chevronRightIcon
    })
  });
  const chevron = hasChildren ? handleToggle != null ?
  /*#__PURE__*/
  // Real toggle button whenever expand/collapse is supported, so the row
  // can be expanded from the keyboard even when the item has no onClick/href
  // (row-level onClick is the only click path in that case, but there is no
  // focusable element to receive Enter/Space). The row's handleClick ignores
  // clicks originating inside a <button>, so this never double-toggles.
  _jsx("button", {
    type: "button",
    "aria-expanded": isExpanded,
    "aria-label": t('@astryx.treeList.toggleChildren')
    // Stable identity for TreeList's activateItem selector — do not
    // remove without also updating TreeList.tsx.
    ,
    "data-tree-toggle": "",
    disabled: isDisabled
    // Roving tabindex lives on the treeitem row; the chevron toggle is not
    // a separate tab stop. Row-level Enter/Space forwards to this button.
    ,
    tabIndex: -1,
    onClick: handleToggle,
    ...mergeProps(
    // Stable theme target for the expand/collapse control. `data-tree-toggle`
    // stays as the functional activation hook; this adds a themeable
    // `astryx-tree-list-chevron` class and reflects the open/closed state so
    // a theme can restyle the toggle (and each state) without a fragile
    // `[data-tree-toggle]` selector.
    themeProps('tree-list-chevron', {
      state: isExpanded ? 'expanded' : 'collapsed'
    }), {
      className: "x78zum5 x6s0dn4 xl56j7k x12xnipv x6b6gus xho7bvi x1ypdohk xv9yike xx3sua9 x11g1kdw xmequkd"
    }),
    children: chevronIcon
  }) :
  /*#__PURE__*/
  // Non-interactive chevron only when toggling is not wired up at all
  _jsx("span", {
    ...mergeProps(themeProps('tree-list-chevron', {
      state: isExpanded ? 'expanded' : 'collapsed'
    }), {
      className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x12xnipv x6b6gus xho7bvi x1ypdohk x1717udv xv9yike xx3sua9 x11g1kdw xmequkd"
    }),
    children: chevronIcon
  }) : null;
  const innerContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [chevron, startContent != null && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4"
      },
      children: startContent
    }), href != null ? /*#__PURE__*/_jsx(LinkComponent, {
      href: href,
      target: target,
      "aria-disabled": isDisabled || undefined,
      "aria-labelledby": labelId,
      "aria-describedby": description != null ? descriptionId : undefined
      // Roving tabindex lives on the treeitem row; inner action is not a
      // separate tab stop. Activation is forwarded from the row.
      ,
      tabIndex: -1,
      ...{
        className: "xmper1u xln7xf2 x1heor9g x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h x1hl2dhg x1a2a7pz"
      },
      children: labelAndDescription
    }) : onClick != null ? /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: onClick,
      disabled: isDisabled,
      "aria-labelledby": labelId,
      "aria-describedby": description != null ? descriptionId : undefined
      // Roving tabindex lives on the treeitem row; inner action is not a
      // separate tab stop. Activation is forwarded from the row.
      ,
      tabIndex: -1,
      ...{
        className: "xmper1u xln7xf2 x1heor9g x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h x1a2a7pz"
      },
      children: labelAndDescription
    }) : /*#__PURE__*/_jsx("span", {
      ...{
        className: "x78zum5 xdt5ytf x98rzlu xeuugli x1yc453h"
      },
      children: labelAndDescription
    }), endContent != null && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4 xvc5jky"
      },
      children: endContent
    })]
  });
  return /*#__PURE__*/_jsxs("li", {
    role: "treeitem",
    "aria-expanded": hasChildren ? isExpanded : undefined,
    "aria-selected": isSelected || undefined,
    "aria-disabled": isDisabled || undefined,
    "aria-level": nestedLevel + 1,
    "aria-posinset": posInSet,
    "aria-setsize": setSize
    // Roving tabindex: exactly one visible treeitem is tabbable at a time.
    // Disabled items are skipped by the tree keyboard handler but remain
    // in the accessibility tree.
    ,
    tabIndex: isDisabled ? -1 : isTabbable ? 0 : -1,
    "data-tree-id": id,
    "data-tree-level": nestedLevel + 1,
    "data-tree-disabled": isDisabled || undefined,
    ...{
      className: "x3ct3a4 x1ghz6dp x1717udv x1n2onr6 xh8yej3 x1a2a7pz x1hb5tod xtoutpx x1f8er9e x1aefuf3"
    },
    children: [variant !== 'noGuides' && /*#__PURE__*/_jsx("div", {
      ...{
        className: "x12gdq22"
      },
      children: /*#__PURE__*/_jsx(TreeListBranches, {
        ancestorsIsLast: ancestorsIsLast,
        isLast: isLast,
        nestedLevel: nestedLevel
      })
    }), /*#__PURE__*/_jsx("div", {
      ...{
        className: "x1n2onr6"
      },
      children: /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('tree-list-item', {
          density,
          selected: isSelected ? 'selected' : null,
          disabled: isDisabled ? 'disabled' : null
        }), stylex.props(styles.contentWrapper, densityStyles[density], (isInteractive || hasChildren && onClick == null) && styles.interactive, (isInteractive || hasChildren && onClick == null) && styles.focusVisibleOutline, isDisabled && styles.disabled, isSelected && styles.selected)),
        style: indentStyle,
        onClick: handleClick,
        children: innerContent
      })
    }), isExpanded && renderedChildren != null && /*#__PURE__*/_jsx("ul", {
      role: "group",
      ...{
        className: "x1ghz6dp x1717udv x3ct3a4"
      },
      children: renderedChildren
    })]
  });
}
TreeListItem.displayName = 'TreeListItem';